/**
 * 公式値の入力・申請（1 品）。
 *
 * 流れ: 入力 → 保存 → 申請 → 運営が承認 → 翌朝カタログへ反映（ROADMAP §🎯 セルフサーブβ）。
 * 申請中・公開済みは読み取り専用（サーバも PUT を 409 で止める — 運営が見た値と公開される値をずらさない）。
 *
 * 検算（4P+9F+4C と申告 kcal の 30% ずれ）は**サーバ・夜間 merge と同じ関数**（`@kalori/nutrition/merge`）で
 * その場に出す。⚠️ 警告で申請は止めない（低糖質・高脂質など正当に外れる商品がある）が、
 * ずれたまま申請するときは 1 回確認を挟む — 事業者の入力は検算にも AI 上書きにも掛からず、
 * 桁違い（498 のつもりが 4,980）を止められるのは事業者自身と運営の承認だけなので。
 */
import { useEffect, useMemo, useState } from "react";
import { hasNutritionInconsistency } from "@kalori/nutrition/quality";
import { ALLERGEN_LIST_VERSION, VALUE_BASES, VALUE_CHECK_LABEL, valueCheckWarnings } from "@kalori/business/brands";
import { api, type CatalogItem, type Me, type MenuEntry, type Nutrition, type NutritionKey, type Preview, type SubmitWarning } from "../api";
import { Link, navigate } from "../router";
import {
  CATALOG_STATE_LABEL,
  NUTRITION_FIELDS,
  REVIEW_STATE_LABEL,
  SUBMIT_BLOCKER_LABEL,
  SUBMIT_WARNING_LABEL,
  fmtDateTime,
  fmtNumber,
  pfcDerivedCal,
} from "../format";
import { SourceBadge, StateChip } from "./parts";
import { RecipeCalculator, savedRecipe } from "./RecipeCalculator";
import { AllergenEditor } from "./AllergenEditor";
import { DocumentsCard } from "./DocumentsCard";
import { PhotoCard } from "./PhotoCard";
import { CopyRecipe } from "./CopyRecipe";
import type { AllergenState, RecipeCalc } from "../api";


type Form = Record<NutritionKey, string>;

function toForm(n: Nutrition): Form {
  const out = {} as Form;
  for (const f of NUTRITION_FIELDS) out[f.key] = n[f.key] == null ? "" : String(n[f.key]);
  return out;
}

/** 表示・保存の桁（エネルギーは整数・ほかは小数第 1 位） */
function roundFor(key: NutritionKey, v: number): number {
  return key === "cal" ? Math.round(v) : Math.round(v * 10) / 10;
}

/** 全角数字・全角ピリオド・カンマ区切りを受ける。空欄 = null。数でなければ undefined（エラー）。 */
function parseValue(raw: string): number | null | undefined {
  const s = raw
    .replace(/[０-９．]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/[,，\s]/g, "");
  if (s === "") return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return undefined;
  return Number(s);
}

export function EntryEditor({ id }: { id: string }) {
  const [entry, setEntry] = useState<MenuEntry | null | undefined>(undefined);
  const [product, setProduct] = useState<CatalogItem | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  /** アレルゲンの 4 状態（品目 → 状態・無い品目は未確認） */
  const [allergenItems, setAllergenItems] = useState<Record<string, AllergenState>>({});
  /** 「材料から計算」で自社の食材・部品から伝わった候補 */
  const [allergenCandidates, setAllergenCandidates] = useState<Record<string, "contains" | "may_contain">>({});
  /** 入力の単位: 1 食あたり / 100g あたり（保存は常に 1 食あたり） */
  const [unit, setUnit] = useState<"serving" | "per100">("serving");
  const [servingGrams, setServingGrams] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableUntil, setAvailableUntil] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<SubmitWarning[]>([]);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [valueBasis, setValueBasis] = useState("");
  const [basisNote, setBasisNote] = useState("");
  const [me, setMe] = useState<Me | null>(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  /** 「材料から計算」で入れた根拠の材料と重量。undefined = 触っていない（保存で送らない） */
  const [components, setComponents] = useState<unknown[] | undefined>(undefined);

  const apply = (e: MenuEntry) => {
    setEntry(e);
    setForm(toForm(e.nutrition));
    // 4 状態が無い古い行は、名前の配列（含むもの）から起こす
    setAllergenItems(e.allergenStates?.items ?? Object.fromEntries((e.allergens ?? []).map((a) => [a, "contains" as const])));
    setUnit("serving");
    setServingGrams(e.servingGrams == null ? "" : String(e.servingGrams));
    setAvailableFrom(e.availableFrom ?? "");
    setAvailableUntil(e.availableUntil ?? "");
    setValueBasis(e.valueBasis ?? "");
    setBasisNote(e.basisNote ?? "");
    setDirty(false);
    setConfirmMismatch(false);
    setPreview(null);
    setComponents(undefined);
  };

  useEffect(() => {
    void api.me().then((r) => r.ok && setMe(r.data));
  }, []);

  useEffect(() => {
    let alive = true;
    void api.entry(id).then(async (r) => {
      if (!alive) return;
      if (!r.ok) {
        setEntry(null);
        return setError(r.error);
      }
      apply(r.data);
      if (r.data.productDbId != null) {
        const p = await api.catalogItem(r.data.productDbId);
        if (alive && p.ok) setProduct(p.data);
      }
    });
    return () => {
      alive = false;
    };
  }, [id]);

  /**
   * 入力欄の値 → 1 食あたりの値（保存・検算は常に 1 食あたり）。100g あたりで入力しているときは 1 食の重量で換算する
   * （規格書・分析値は 100g あたりのことが多く、1 食あたりの欄に入れ間違える事故の最有力 — だから換算は画面がやる）。
   */
  const parsed = useMemo(() => {
    const typed = {} as Record<NutritionKey, number | null>;
    const invalid: NutritionKey[] = [];
    if (form) {
      for (const f of NUTRITION_FIELDS) {
        const v = parseValue(form[f.key]);
        if (v === undefined) invalid.push(f.key);
        typed[f.key] = v ?? null;
      }
    }
    const sg = parseValue(servingGrams);
    const serving = sg != null && sg !== undefined && sg > 0 ? sg : null;
    const servingInvalid = servingGrams.trim() !== "" && serving == null;
    if (unit === "serving") return { values: typed, invalid, serving, servingInvalid, needServing: false };
    const values = {} as Record<NutritionKey, number | null>;
    for (const f of NUTRITION_FIELDS) {
      const v = typed[f.key];
      values[f.key] = v == null || serving == null ? null : roundFor(f.key, (v * serving) / 100);
    }
    return { values, invalid, serving, servingInvalid, needServing: serving == null };
  }, [form, unit, servingGrams]);

  if (entry === undefined) return <p className="muted">読み込んでいます…</p>;
  if (entry === null || !form)
    return (
      <section className="stack">
        <p className="error">{error ?? "見つかりません。"}</p>
        <Link href="/entries">公式値の入力へ戻る</Link>
      </section>
    );

  const editable = entry.reviewState === "editing";
  const v = parsed.values;
  const derived = pfcDerivedCal(v.protein, v.fat, v.carbs);
  const mismatch = hasNutritionInconsistency(v.cal, v.protein, v.fat, v.carbs);
  // PFC の外側の検算（申請・承認画面と同じ関数・@kalori/business/brands）
  const checks = valueCheckWarnings({ ...v, servingGrams: parsed.serving });

  /** 入力の単位を切り替える。1 食の重量があれば、入っている値を換算して残す */
  const switchUnit = (next: "serving" | "per100") => {
    if (next === unit) return;
    const sg = parsed.serving;
    if (sg != null) {
      const out = {} as Form;
      for (const f of NUTRITION_FIELDS) {
        // parsed.values は常に 1 食あたり。100g あたりへは 1 食の重量で割り戻す
        const cur = parsed.values[f.key];
        out[f.key] = cur == null ? "" : String(next === "per100" ? roundFor(f.key, (cur * 100) / sg) : cur);
      }
      setForm(out);
    }
    setUnit(next);
  };

  /**
   * 「材料から計算」の結果を入力欄へ（ボタンを押したときだけ）。計算できなかった項目（null）は欄を変えない。
   * 根拠: 推定から始めたなら「推定」・自分の材料なら「レシピから計算」。補足が空なら計算の出典を入れる。
   */
  const applyRecipe = (calc: RecipeCalc, fromEstimate: boolean) => {
    // 計算は 1 食あたりで返るので、入力欄も 1 食あたりに戻す
    const next = unit === "serving" ? { ...form } : toForm(parsed.values);
    for (const f of NUTRITION_FIELDS) {
      const val = calc.perServing[f.key];
      if (val != null) next[f.key] = String(val);
    }
    setUnit("serving");
    setForm(next);
    if (!servingGrams.trim() && calc.perServingGrams > 0) setServingGrams(String(calc.perServingGrams));
    setAllergenCandidates(calc.allergenCandidates);
    setValueBasis(fromEstimate ? "estimated" : "recipe_calculated");
    if (!basisNote.trim()) {
      setBasisNote(
        fromEstimate
          ? "kalori の推定（材料と重量）をもとに計算"
          : `材料 ${calc.items.length} 品・${calc.servings} 人前のレシピから計算（日本食品標準成分表（八訂）増補）`,
      );
    }
    setComponents(calc.components);
    setDirty(true);
    setMessage(
      fromEstimate
        ? "計算結果を入れました。推定の重量のままなので、値の根拠は「上記以外の推定」にしています。保存してください。"
        : "計算結果を入れました。保存してください。",
    );
  };

  const save = async (): Promise<boolean> => {
    if (parsed.invalid.length > 0) {
      setError("数字で入力してください（空欄にすると、その項目はいまの掲載値のままです）。");
      return false;
    }
    if (parsed.servingInvalid) {
      setError("1 食の重量は g の数字で入力してください。");
      return false;
    }
    if (parsed.needServing) {
      setError("100g あたりで入力するときは、1 食の重量（g）を入れてください（1 食あたりに換算して保存します）。");
      return false;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    const r = await api.updateEntry(entry.id, {
      nutrition: parsed.values,
      allergenStates: { version: entry.allergenStates?.version ?? ALLERGEN_LIST_VERSION, items: allergenItems },
      servingGrams: parsed.serving,
      availableFrom: availableFrom || null,
      availableUntil: availableUntil || null,
      valueBasis: valueBasis || null,
      basisNote: basisNote.trim() || null,
      ...(components !== undefined ? { components } : {}),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return false;
    }
    // 保存の応答（PUT）は改訂の元を持たないので、開いたときの値を引き継ぐ
    apply({ ...r.data, revisionOf: r.data.revisionOf ?? entry.revisionOf });
    setMessage("保存しました。");
    return true;
  };

  const submit = async () => {
    if (!valueBasis) {
      setError("値の根拠を選んでください（申請のときにしか伺えない情報です）。");
      return;
    }
    if (me && !me.terms.accepted) {
      if (!me.terms.currentVersion) {
        setError("利用規約を準備しているため、申請はまだできません（入力と保存はできます）。");
        return;
      }
      if (!agreeTerms) {
        setError("利用規約への同意が必要です。");
        return;
      }
    }
    if (mismatch && !confirmMismatch) {
      setConfirmMismatch(true);
      return;
    }
    if (dirty && !(await save())) return;
    if (me && !me.terms.accepted && me.terms.currentVersion) {
      const accepted = await api.acceptTerms(me.terms.currentVersion);
      if (!accepted.ok) return setError(accepted.error);
      setMe(accepted.data);
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    const r = await api.submit(entry.id);
    if (!r.ok) {
      setBusy(false);
      const blockers = Array.isArray(r.body?.blockers) ? (r.body.blockers as string[]) : [];
      setError(blockers.length ? blockers.map((b) => SUBMIT_BLOCKER_LABEL[b] ?? b).join(" ") : r.error);
      return;
    }
    setWarnings(r.data.warnings);
    const fresh = await api.entry(entry.id);
    setBusy(false);
    if (fresh.ok) apply(fresh.data);
    setMessage("申請しました。運営が確認し、承認の翌朝に kalori のカタログへ反映します。");
  };

  /** 公開中の値の改訂を始める（改訂の下書きへ移る。既に下書きがあればそれを開く） */
  const revise = async () => {
    setBusy(true);
    setError(null);
    const r = await api.reviseEntry(entry.id);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    navigate(`/entries/${r.data.id}`);
  };

  const withdraw = async () => {
    setBusy(true);
    const r = await api.withdraw(entry.id);
    const fresh = await api.entry(entry.id);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    if (fresh.ok) apply(fresh.data);
    setWarnings([]);
    setMessage("申請を取り下げました。編集できます。");
  };

  const remove = async () => {
    if (!window.confirm("この入力を削除します。よろしいですか？（kalori の掲載値は変わりません）")) return;
    setBusy(true);
    const r = await api.deleteEntry(entry.id);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    navigate("/entries", { replace: true });
  };

  const showPreview = async () => {
    if (dirty && !(await save())) return;
    const r = await api.preview(entry.id);
    if (r.ok) setPreview(r.data);
    else setError(r.error);
  };

  const setField = (key: NutritionKey, value: string) => {
    setForm({ ...form, [key]: value });
    setDirty(true);
    setConfirmMismatch(false);
  };

  return (
    <section className="stack-lg">
      <div>
        <p className="small">
          <Link href="/entries">← 公式値の入力</Link>
        </p>
        <div className="row-between">
          <h1>{entry.name}</h1>
          <StateChip state={entry.reviewState} label={REVIEW_STATE_LABEL[entry.reviewState]} />
        </div>
        <p className="muted small">
          {entry.productDbId != null && (
            <>
              <Link href={`/catalog/${entry.productDbId}`}>掲載中のメニューを見る</Link>
              {" ・ "}
            </>
          )}
          更新 {fmtDateTime(entry.updatedAt)}
          {entry.submittedAt && ` ・ 申請 ${fmtDateTime(entry.submittedAt)}`}
          {entry.publishedAt && ` ・ 公開 ${fmtDateTime(entry.publishedAt)}`}
        </p>
      </div>

      {entry.reviewState === "editing" && entry.lastDecision?.event === "rejected" && (
        <div className="error">
          <strong>運営から差し戻しがありました（{fmtDateTime(entry.lastDecision.at)}）</strong>
          <div>{entry.lastDecision.reasonLabel ?? "理由: その他"}</div>
          {entry.lastDecision.note && <div className="pre">{entry.lastDecision.note}</div>}
          <div className="small">直して、もう一度申請してください。</div>
        </div>
      )}
      {entry.reviewState === "pending_review" && (
        <p className="notice">審査待ちです。運営が確認しています。直したいときは申請を取り下げてから編集してください。</p>
      )}
      {entry.reviewState === "published" && entry.catalogState !== "gone" && (
        <div className="notice row-between">
          <span>公開済みです。値を変えたいときは改訂を申請してください。承認されるまで、いまの値がそのまま使われます。</span>
          <button className="btn primary" disabled={busy} onClick={() => void revise()}>
            値を改訂する
          </button>
        </div>
      )}
      {entry.revisionOf && entry.reviewState !== "published" && (
        <div className="notice">
          <strong>公開中の値の改訂です</strong>
          <div className="small">
            承認されると、いま公開している値（
            {NUTRITION_FIELDS.filter((f) => entry.revisionOf!.nutrition[f.key] != null)
              .map((f) => `${f.label} ${fmtNumber(entry.revisionOf!.nutrition[f.key], f.unit)}`)
              .join("・")}
            ）と入れ替わります。それまでは公開中の値のままです。
          </div>
        </div>
      )}
      {entry.reviewState === "unpublished" && (
        <div className="notice">
          <strong>公開を止めました{entry.lastDecision?.event === "unpublished" && `（${fmtDateTime(entry.lastDecision.at)}）`}</strong>
          {entry.lastDecision?.event === "unpublished" && entry.lastDecision.note && <div className="pre">{entry.lastDecision.note}</div>}
          <div className="small">翌朝までに、掲載値は御社サイトの値・kalori の推定へ戻ります。</div>
        </div>
      )}
      {(entry.catalogState === "gone" || entry.catalogState === "leaving") &&
        entry.reviewState !== "unpublished" &&
        entry.reviewState !== "archived" && (
          <p className={entry.catalogState === "gone" ? "error" : "notice"}>
            {CATALOG_STATE_LABEL[entry.catalogState].text} <Link href="/catalog">掲載中のメニューを見る</Link>
          </p>
        )}

      <div className="card">
        <div className="row-between">
          <h2>栄養成分</h2>
          <div className="segmented" role="group" aria-label="入力の単位">
            <button type="button" className={unit === "serving" ? "on" : ""} disabled={!editable} onClick={() => switchUnit("serving")}>
              1 食あたりで入力
            </button>
            <button type="button" className={unit === "per100" ? "on" : ""} disabled={!editable} onClick={() => switchUnit("per100")}>
              100g あたりで入力
            </button>
          </div>
        </div>
        <p className="muted small">
          御社の公式値を入力してください。空欄の項目は、いまの掲載値（右の参考）のまま変わりません。
          {unit === "per100" && " 100g あたりの値は、1 食の重量で 1 食あたりに換算して保存します。"}
        </p>
        <label className="field inline-field">
          <span>1 食の重量</span>
          <span className="input-unit">
            <input
              inputMode="decimal"
              value={servingGrams}
              disabled={!editable}
              aria-invalid={parsed.servingInvalid || (unit === "per100" && parsed.needServing)}
              onChange={(e) => {
                setServingGrams(e.target.value);
                setDirty(true);
              }}
              aria-label="1 食の重量"
            />
            <span className="unit">g</span>
          </span>
          <span className="muted small">{unit === "per100" ? "（必須）" : "（任意・重さの検算に使います）"}</span>
        </label>
        <table className="table form-table">
          <thead>
            <tr>
              <th>項目</th>
              <th>公式値（{unit === "per100" ? "100g あたり" : "1 食あたり"}）</th>
              {unit === "per100" && <th className="num">1 食あたり</th>}
              {entry.revisionOf && <th className="num">いま公開中</th>}
              <th className="hide-sm">参考: いまの掲載値</th>
            </tr>
          </thead>
          <tbody>
            {NUTRITION_FIELDS.map((f) => {
              const published = entry.revisionOf?.nutrition[f.key] ?? null;
              const changed = entry.revisionOf != null && v[f.key] != null && published !== v[f.key];
              return (
                <tr key={f.key}>
                  <th>
                    <label htmlFor={`n-${f.key}`}>{f.label}</label>
                  </th>
                  <td>
                    <span className="input-unit">
                      <input
                        id={`n-${f.key}`}
                        inputMode="decimal"
                        value={form[f.key]}
                        disabled={!editable}
                        aria-invalid={parsed.invalid.includes(f.key)}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                      <span className="unit">{f.unit}</span>
                    </span>
                  </td>
                  {unit === "per100" && <td className="num">{fmtNumber(v[f.key], f.unit)}</td>}
                  {entry.revisionOf && (
                    <td className={changed ? "num changed" : "num muted"} title={changed ? "改訂で変わる値" : undefined}>
                      {fmtNumber(published, f.unit)}
                    </td>
                  )}
                  <td className="hide-sm">
                    {product ? (
                      <>
                        {fmtNumber(product.nutrition[f.key], f.unit)} <SourceBadge source={product.provenance[f.key]} />
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={mismatch || checks.length > 0 ? "check warn" : "check"}>
          {derived == null ? (
            <span className="muted small">たんぱく質・脂質・炭水化物を入れると、エネルギーを検算します。</span>
          ) : (
            <span className="small">
              たんぱく質・脂質・炭水化物から計算すると約 <strong>{fmtNumber(derived, "kcal")}</strong>
              {v.cal != null && `（入力 ${fmtNumber(v.cal, "kcal")}）`}
              {mismatch && " — 30% 以上ずれています。桁や単位をご確認ください。"}
            </span>
          )}
          {checks.map((k) => (
            <div key={k} className="small">
              ⚠️ {VALUE_CHECK_LABEL[k]}
            </div>
          ))}
        </div>
      </div>

      <RecipeCalculator
        entryName={entry.name}
        saved={entry.components}
        estimate={product?.estimation?.components ?? null}
        editable={editable}
        onApply={applyRecipe}
      />

      {savedRecipe(entry.components) && <CopyRecipe entryId={entry.id} excludeProductId={entry.productDbId} />}

      <AllergenEditor
        items={allergenItems}
        editable={editable}
        candidates={allergenCandidates}
        onChange={(next) => {
          setAllergenItems(next);
          setDirty(true);
        }}
      />

      <div className="card">
        <h2>値の根拠</h2>
        <p className="muted small">この値がどこから来たものかを選んでください。承認後も、値とあわせて記録に残ります（「公式」に書き換えません）。</p>
        <div className="stack">
          <label className="field">
            <span className="small">根拠</span>
            <select
              value={valueBasis}
              disabled={!editable}
              onChange={(e) => {
                setValueBasis(e.target.value);
                setDirty(true);
              }}
            >
              <option value="">選んでください</option>
              {Object.entries(VALUE_BASES).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <div className="toolbar">
            <label className="field">
              <span className="small">提供開始（任意）</span>
              <input
                type="date"
                value={availableFrom}
                disabled={!editable}
                onChange={(e) => {
                  setAvailableFrom(e.target.value);
                  setDirty(true);
                }}
              />
            </label>
            <label className="field">
              <span className="small">提供終了（任意・期間限定メニュー）</span>
              <input
                type="date"
                value={availableUntil}
                disabled={!editable}
                onChange={(e) => {
                  setAvailableUntil(e.target.value);
                  setDirty(true);
                }}
              />
            </label>
          </div>
          <p className="muted small">提供終了を過ぎても公開中の公式値は「変化の知らせ」に出し、運営にもお知らせします（公開を止めるかは運営と相談）。</p>
          <label className="field">
            <span className="small">補足（任意。分析の時期・規格書の版など）</span>
            <input
              value={basisNote}
              maxLength={500}
              disabled={!editable}
              onChange={(e) => {
                setBasisNote(e.target.value);
                setDirty(true);
              }}
            />
          </label>
        </div>
      </div>

      <PhotoCard
        entry={entry}
        editable={editable}
        onChanged={() =>
          // 写真まわりだけ差し替える（入力中の値を読み直しで消さない）
          void api.entry(entry.id).then((r) => {
            if (r.ok) setEntry((cur) => (cur ? { ...cur, photo: r.data.photo, catalogImage: r.data.catalogImage, revisionOf: r.data.revisionOf } : cur));
          })
        }
      />

      <DocumentsCard entryId={entry.id} />

      {editable && me && !me.terms.accepted && (
        <div className="card">
          <h2>利用規約</h2>
          {me.terms.currentVersion ? (
            <label className="check-item">
              <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
              <span>
                <a href={me.terms.url} target="_blank" rel="noopener noreferrer">
                  利用規約（飲食ブランド向け補遺）
                </a>
                に同意します。承認された値は kalori のアプリ・サイトに掲載され、kalori のカタログ API を通じて外部のサービスにも提供されます。
              </span>
            </label>
          ) : (
            <p className="muted small">利用規約を準備しています。公開までは申請できません（入力と保存はできます）。</p>
          )}
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {message && !error && <p className="notice">{message}</p>}
      {warnings.length > 0 && (
        <ul className="warnings">
          {warnings.map((w) => (
            <li key={w}>{SUBMIT_WARNING_LABEL[w]}</li>
          ))}
        </ul>
      )}
      {confirmMismatch && (
        <p className="notice">
          エネルギーと計算値が 30% 以上ずれています。低糖質の商品などで正しい場合は、もう一度「申請する」を押してください。
        </p>
      )}

      <div className="actions">
        {editable && (
          <>
            <button className="btn" disabled={busy || !dirty} onClick={() => void save()}>
              保存
            </button>
            <button className="btn" disabled={busy} onClick={() => void showPreview()}>
              カタログでの表示を確認
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void submit()}>
              {confirmMismatch ? "確認のうえ申請する" : "申請する"}
            </button>
            <span className="spacer" />
            <button className="btn danger" disabled={busy} onClick={() => void remove()}>
              削除
            </button>
          </>
        )}
        {entry.reviewState === "pending_review" && (
          <button className="btn" disabled={busy} onClick={() => void withdraw()}>
            申請を取り下げる
          </button>
        )}
      </div>

      {preview && (
        <div className="card">
          <h2>承認後のカタログでの表示</h2>
          <p className="muted small">入力した項目は「御社入力」に置き換わり、空欄の項目はいまの値のまま残ります。</p>
          <table className="table kv">
            <tbody>
              {NUTRITION_FIELDS.map((f) => {
                const key = f.key === "cal" ? "heat" : f.key;
                return (
                  <tr key={f.key}>
                    <th>{f.label}</th>
                    <td className="num">{fmtNumber(preview.nutrition[key] ?? null, f.unit)}</td>
                    <td>
                      <SourceBadge source={preview.sourceMap?.[key] ?? null} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
